// Generated by DesignToCode

import {
  Component,
  computed,
  effect,
  input,
  output,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  FormsModule,
} from '@angular/forms';
import { AngularSvgIconModule } from 'angular-svg-icon';

@Component({
  selector: 'app-checkbox',
  standalone: true,
  imports: [CommonModule, FormsModule, AngularSvgIconModule],
  templateUrl: './checkbox.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: Checkbox,
      multi: true,
    },
  ],
})
export class Checkbox implements ControlValueAccessor {
  // Signal inputs
  public disabled = input(false);
  public checked = input(false);

  public onValueChanged = output<boolean>();

  // Internal writable signals for ControlValueAccessor
  public isChecked = signal(false);
  public isDisabled = signal(false);

  // UI state signals
  private focused = signal(false);
  private hovered = signal(false);
  private isPressed = signal(false);

  public icon = computed(() =>
    this.disabled() ? 'checkbox-disabled.svg' : 'icon_checkmark_2_16px.svg',
  );

  private formValueInitialized = false;

  // Computed classes
  public parentContainerClasses = computed(() => {
    const classes: string[] = ['flex', 'items-center', 'gap-xs', 'w-fit'];
    if (!this.isDisabled()) {
      classes.push('cursor-pointer');
    } else {
      classes.push('cursor-not-allowed');
    }

    return classes.join(' ');
  });

  public checkboxContainerClasses = computed(() => {
    // Base classes
    const classes: string[] = [
      'w-4',
      'h-4',
      'flex',
      'items-center',
      'justify-center',
      'rounded-[3px]',
      'border-[1.5px]',
    ];

    if (this.isDisabled()) {
      if (this.isChecked()) {
        // Selected Disabled state
        classes.push('bg-grey-80', 'border-grey-80');
      } else {
        // Disabled state
        classes.push('bg-navy-blue-60', 'border-navy-blue-60');
      }
    } else if (this.isChecked()) {
      // Selected state
      classes.push('bg-navy-blue-90', 'border-navy-blue-90');
    } else {
      // Default state
      classes.push('bg-transparent', 'border-grey-90');
    }

    return classes.join(' ');
  });

  public checkmarkClasses = computed(() => {
    const classes: string[] = ['fill-white'];
    return classes.join(' ');
  });

  public labelClasses = computed(() => {
    const classes: string[] = [
      'font-caption',
      'font-medium',
      'text-sm',
      'leading-lg',
    ];

    if (this.isDisabled()) {
      if (this.isChecked()) {
        // Selected Disabled state
        classes.push('text-grey-80');
      } else {
        // Disabled state
        classes.push('text-grey-110');
      }
    } else if (this.isChecked()) {
      // Selected state
      classes.push('text-grey-120');
    } else {
      // Default state
      classes.push('text-grey-110');
    }

    return classes.join(' ');
  });

  constructor() {
    // Initialize internal states from inputs
    effect(() => {
      const checkValue = this.checked();
      if (!this.formValueInitialized) {
        this.isChecked.set(checkValue);
      }
    });

    effect(() => {
      this.isDisabled.set(this.disabled());
    });
  }

  // ControlValueAccessor implementation
  private onChange: any = () => {};
  private onTouched: any = () => {};

  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  public writeValue(value: boolean): void {
    this.formValueInitialized = true;
    this.isChecked.set(value);
  }

  public setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
  }

  // Event handlers
  public onCheckboxChange(event: Event): void {
    if (this.isDisabled()) return;

    const newValue = !this.isChecked();
    this.isChecked.set(newValue);
    this.onChange(newValue);
    this.onTouched();
    this.onValueChanged.emit(newValue);
  }

  public onFocus(): void {
    if (this.isDisabled()) return;
    this.focused.set(true);
  }

  public onBlur(): void {
    this.focused.set(false);
    this.onTouched();
  }

  public onMouseDown(): void {
    if (this.isDisabled()) return;
    this.isPressed.set(true);
  }

  public onMouseUp(): void {
    this.isPressed.set(false);
  }

  public onMouseEnter(): void {
    if (this.isDisabled()) return;
    this.hovered.set(true);
  }

  public onMouseLeave(): void {
    this.hovered.set(false);
    this.isPressed.set(false);
  }
}
